import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { theme } from '../components/theme';
import { useNavigate } from 'react-router-dom';

export const Header = ({ onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const menuItems = [
    { label: 'Features', key: 'features' },
    { label: 'Products', url: '/products' },
    // { label: 'Services', key: 'services' },
    { label: 'Pricing', key: 'pricing' },
  ];

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : theme.colors.neutral.white,
        backdropFilter: isScrolled ? 'blur(10px)' : 'none',
        boxShadow: isScrolled ? theme.shadows.md : 'none',
        transition: `all ${theme.transitions.base}`,
      }}
    >
      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '72px',
        }}
      >
        <div
          onClick={() => onNavigate('/')}
          style={{
            fontSize: theme.typography.fontSize['2xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.primary.navy,
            cursor: 'pointer',
            transition: `color ${theme.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = theme.colors.primary.orange)}
          onMouseLeave={(e) => (e.currentTarget.style.color = theme.colors.primary.navy)}
        >
          Aptify
        </div>

        <nav
          style={{
            display: 'flex',
            gap: theme.spacing.xl,
            alignItems: 'center',
          }}
          className="hidden md:flex"
        >
          {menuItems.map((item) => (
            <button
                key={item?.key}
                onClick={() => {
                  if (item?.key) scrollToSection(item?.key);
                  if (item?.url) navigate(item?.url);
                }}
              style={{
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.primary.navy,
                textDecoration: 'none',
                transition: `color ${theme.transitions.fast}`,
                cursor: 'pointer',
                backgroundColor: 'transparent',
                border: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = theme.colors.primary.orange)}
              onMouseLeave={(e) => (e.currentTarget.style.color = theme.colors.primary.navy)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '10px 24px',
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.primary.navy,
              backgroundColor: 'transparent',
              border: `2px solid ${theme.colors.primary.navy}`,
              borderRadius: theme.borderRadius.md,
              cursor: 'pointer',
              transition: `all ${theme.transitions.fast}`,
            }}
            className="hidden md:block"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary.navy;
              e.currentTarget.style.color = theme.colors.neutral.white;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = theme.colors.primary.navy;
            }}
          >
            Sign In
          </button>
          <button
            // onClick={() => navigate('/register')}
            style={{
              padding: '10px 24px',
              fontSize: theme.typography.fontSize.base,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.neutral.white,
              backgroundColor: theme.colors.primary.orange,
              border: 'none',
              borderRadius: theme.borderRadius.md,
              cursor: 'pointer',
              transition: `all ${theme.transitions.fast}`,
            }}
            className="hidden md:block"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary.orangeLight;
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = theme.shadows.lg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary.orange;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Get Started Free
          </button>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              padding: theme.spacing.sm,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary.navy,
            }}
            className="md:hidden"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          style={{
            backgroundColor: theme.colors.neutral.white,
            borderTop: `1px solid ${theme.colors.neutral.gray100}`,
            padding: theme.spacing.lg,
          }}
          className="md:hidden"
        >
          <nav style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            {menuItems.map((item) => (
              <button
                key={item?.key}
                onClick={() => {
                  if (item?.key) scrollToSection(item?.key);
                  setIsMobileMenuOpen(false);
                  if (item?.url) navigate(item?.url);
                }}
                style={{
                  fontSize: theme.typography.fontSize.base,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.primary.navy,
                  textDecoration: 'none',
                  padding: theme.spacing.md,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => {
                navigate('/login');
                setIsMobileMenuOpen(false);
              }}
              style={{
                padding: '12px 24px',
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.primary.navy,
                backgroundColor: 'transparent',
                border: `2px solid ${theme.colors.primary.navy}`,
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer',
              }}
            >
              Sign In
            </button>
            <button
              onClick={() => {
                navigate('/register');
                setIsMobileMenuOpen(false);
              }}
              style={{
                padding: '12px 24px',
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.neutral.white,
                backgroundColor: theme.colors.primary.orange,
                border: 'none',
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer',
              }}
            >
              Get Started Free
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
